/**
 * La ficha del cliente: quien es, cuanto debe y en que moneda esta parada esa
 * deuda.
 *
 * LA REGLA QUE ORDENA TODO ESTE ARCHIVO
 * -------------------------------------
 * Un cliente con la cuenta anclada al dolar tiene DOS numeros y los dos son
 * ciertos:
 *
 *   · los dolares que debe  — la deuda, la que no cambia sola;
 *   · los pesos de hoy      — lo que hay que cobrarle si viene esta tarde.
 *
 * Mostrar uno solo es mentir por omision en cualquiera de las dos direcciones:
 * con los dolares solos, el que cobra no sabe cuanta plata pedir; con los pesos
 * solos, manaña el numero cambio y parece un error del sistema. Van los dos, y
 * al lado del de pesos va SIEMPRE la cotizacion con la que se llego.
 *
 * El saldo lleva SIEMPRE el color de su moneda. "A favor" se dice con un badge
 * al lado y con el signo, nunca pintando el numero de verde (MASTER seccion 3.2).
 */
import {
  Banknote,
  CalendarClock,
  Mail,
  MapPin,
  Pencil,
  Phone,
  TriangleAlert,
  User,
} from "lucide-react";

import { BadgeMora } from "@/components/cobro/BadgeMora";
import { ChipMoneda, Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import { Kpi } from "@/components/ui/kpi";
import { Progreso } from "@/components/ui/progreso";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { formatearFecha } from "@/lib/utils";
import { aNumero, type Cliente } from "@/lib/tipos";

import {
  creditoDisponible,
  estadoMoraDe,
  leerSaldo,
  monedaAnclaje,
  textoCotizacion,
  type MoraCC,
  type SaldoCC,
} from "./logica";

export interface FichaClienteProps {
  cliente: Cliente;
  saldo: SaldoCC | null;
  mora: MoraCC | null;
  cotizacion: number;
  onEditar: () => void;
  /** Abre el cobro. Sin esto no hay boton: no debe nada que cobrarle. */
  onCobrar?: (() => void) | undefined;
}

export function FichaCliente({
  cliente,
  saldo,
  mora,
  cotizacion,
  onEditar,
  onCobrar,
}: FichaClienteProps): JSX.Element {
  const legible = leerSaldo(saldo, cotizacion);
  const moneda = monedaAnclaje(cliente);

  return (
    <div className="space-y-4">
      {/* Encabezado de ficha (06 seccion 6.1): badge de estado primero, titulo,
          linea de meta, y las acciones a la derecha. */}
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            {!cliente.activo ? <Badge variant="secondary">dado de baja</Badge> : null}
            <TituloSeccion nivel="ficha" className="truncate">
              {cliente.nombre}
            </TituloSeccion>
          </div>
          <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <ChipMoneda moneda={moneda} />
              {moneda === "USD" ? "cuenta anclada al dólar" : "cuenta fija en pesos"}
            </span>
            {cliente.codigo ? (
              <span>
                · <Codigo>{cliente.codigo}</Codigo>
              </span>
            ) : null}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={onEditar}>
            <Pencil aria-hidden="true" />
            Editar
          </Button>
          {/*
            El cobro se abre aca mismo: es el mismo dialogo que el de «Me
            deben», que elige la cuenta que recibe la plata e imputa contra los
            comprobantes viejos. Antes este boton mandaba a otra pantalla solo
            para abrirlo.
          */}
          {onCobrar ? (
            <Button type="button" onClick={onCobrar}>
              <Banknote aria-hidden="true" />
              Cobrarle
            </Button>
          ) : null}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <TarjetaSaldo legible={legible} className="lg:col-span-2" />
        <TarjetaMora mora={mora} saldoArsHoy={legible.cobrableHoyArs} />
      </div>

      {/* La seña de una reparacion abierta se restaba del saldo: el que debia
          y dejo el diagnostico «debia menos», o salia de «Me deben» con saldo
          a favor. Ahora el saldo es la cuenta corriente real y la seña se
          dice aparte. */}
      {aNumero(saldo?.senas_ot) > 0.009 ? (
        <p className="text-sm text-muted-foreground">
          Además dejó{" "}
          <Importe monto={aNumero(saldo?.senas_ot)} moneda={legible.moneda} tamano="sm" /> a cuenta
          de reparaciones que siguen en el taller. No se descuenta de este saldo: se usa al entregar
          el equipo.
        </p>
      ) : null}

      <TarjetaCredito cliente={cliente} saldoAnclaje={legible.monto} cotizacion={cotizacion} />

      <Contacto cliente={cliente} />
    </div>
  );
}

/**
 * El saldo, con sus dos lecturas cuando corresponde.
 *
 * El numero grande es el de la MONEDA DE LA CUENTA, porque es la deuda de
 * verdad. Abajo, en la misma tarjeta y no en un tooltip, va lo que se le cobra
 * hoy en pesos con la cotizacion escrita al lado.
 */
function TarjetaSaldo({
  legible,
  className,
}: {
  legible: ReturnType<typeof leerSaldo>;
  className?: string;
}): JSX.Element {
  const esUsd = legible.moneda === "USD";
  const montoMostrado = Math.abs(legible.monto);

  return (
    <Kpi
      etiqueta={legible.titulo}
      monto={montoMostrado}
      moneda={legible.moneda}
      estado={legible.aFavor ? { texto: "A favor", variant: "success" } : undefined}
      signoExplicito={legible.aFavor}
      className={className}
      nota={
        esUsd ? (
          <>
            <span className="flex flex-wrap items-baseline gap-x-2">
              <span>{legible.aFavor ? "A favor, en pesos de hoy:" : "Para cobrarle hoy:"}</span>
              <Importe
                monto={Math.abs(legible.cobrableHoyArs)}
                moneda="ARS"
                tamano="lg"
                className="font-semibold"
              />
            </span>
            <span className="mt-1 block">
              {legible.cotizacion && legible.cotizacion > 0 ? (
                <>
                  Al dólar de hoy:{" "}
                  <span className="tabular-nums">
                    US$ 1 = $ {textoCotizacion(legible.cotizacion)}
                  </span>
                  . Es una conversión, no una suma: si mañana cambia el dólar, este número cambia y
                  los <Importe monto={montoMostrado} moneda="USD" tamano="sm" codigo={false} />{" "}
                  siguen siendo los mismos.
                </>
              ) : (
                "Sin cotización cargada no se puede decir cuántos pesos son hoy."
              )}
            </span>
          </>
        ) : (
          <>
            {/* Con saldo a favor decia «lo que se le cobra hoy»: no se le cobra
                nada, es plata suya. */}
            Esta cuenta no se mueve con el dólar: cada movimiento quedó congelado a la cotización de
            su día. {legible.aFavor ? "Lo que tiene a favor" : "Lo que se le cobra hoy"} son estos
            mismos <Importe monto={montoMostrado} moneda="ARS" tamano="sm" codigo={false} />.
          </>
        )
      }
    />
  );
}

function TarjetaMora({
  mora,
  saldoArsHoy,
}: {
  mora: MoraCC | null;
  saldoArsHoy: number;
}): JSX.Element {
  const estado = estadoMoraDe({
    estado_mora: mora?.estado_mora,
    antiguedad_deuda: mora?.antiguedad_deuda,
    saldoArsHoy,
  });
  const impagos = aNumero(mora?.comprobantes_impagos);

  return (
    <Card as="section" padding="normal" className="flex flex-col gap-2">
      <TituloSeccion nivel="etiqueta" as="h3">
        Cómo viene
      </TituloSeccion>
      <BadgeMora estado={estado} className="self-start" />

      <dl className="space-y-1.5 text-xs">
        {mora?.antiguedad_deuda ? (
          <Dato
            termino="Comprobante impago más viejo"
            valor={`hace ${mora.antiguedad_deuda} ${mora.antiguedad_deuda === 1 ? "día" : "días"}`}
          />
        ) : null}
        {impagos > 0 ? (
          <Dato
            termino="Comprobantes sin cancelar"
            valor={`${impagos} ${impagos === 1 ? "comprobante" : "comprobantes"}`}
          />
        ) : null}
        <Dato
          termino="Último cobro"
          valor={mora?.ultimo_cobro ? formatearFecha(mora.ultimo_cobro) : "nunca"}
        />
      </dl>

      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <CalendarClock className="mt-px size-3 shrink-0" aria-hidden="true" />
        <span>
          La antigüedad se mide sobre el comprobante impago más viejo, no sobre el último pago: un
          pago chico no la borra.
        </span>
      </p>
    </Card>
  );
}

/**
 * El limite de credito y cuanto queda.
 *
 * El limite puede estar en una moneda y la cuenta en otra (un limite de
 * US$ 5.000 sobre una cuenta en pesos es normal). Cuando eso pasa hay una
 * conversion, y la cotizacion con la que se hizo va escrita: un "te quedan
 * $ 300.000" sin decir a que dolar se calculo no se puede verificar.
 */
function TarjetaCredito({
  cliente,
  saldoAnclaje,
  cotizacion,
}: {
  cliente: Cliente;
  saldoAnclaje: number;
  cotizacion: number;
}): JSX.Element {
  const credito = creditoDisponible({
    limite: cliente.limite_credito,
    limiteMoneda: cliente.limite_moneda,
    saldo: saldoAnclaje,
    monedaSaldo: monedaAnclaje(cliente),
    cotizacion,
  });

  if (!credito) {
    return (
      <Card as="section" variant="dashed" padding="normal" className="space-y-1">
        <TituloSeccion nivel="etiqueta" as="h3">
          Límite de crédito
        </TituloSeccion>
        <p className="text-sm text-muted-foreground">
          Sin tope. Las ventas a cuenta de este cliente no van a disparar ningún aviso por monto.
        </p>
      </Card>
    );
  }

  const pct = Math.min(100, credito.porcentaje);

  return (
    <Card as="section" padding="normal" className="space-y-2">
      <TituloSeccion
        nivel="etiqueta"
        as="h3"
        acciones={
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            {/* Con saldo a favor lo «usado» es negativo, y decia «usa −$ 10.000
                de $ 100.000». No usa nada: tiene el tope entero y algo mas. */}
            {credito.usado > 0 ? (
              <>
                usa <Importe monto={credito.usado} moneda={credito.moneda} tamano="sm" /> de{" "}
              </>
            ) : (
              "no usa nada de "
            )}
            <Importe monto={credito.limite} moneda={credito.moneda} tamano="sm" codigo={false} />
          </span>
        }
      >
        Límite de crédito
      </TituloSeccion>

      <Progreso
        valor={pct}
        tono={credito.excedido ? "destructive" : pct >= 80 ? "warning" : "primary"}
        aria-label={`Consumido el ${credito.porcentaje}% del límite`}
      />

      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {credito.excedido ? (
          <span className="inline-flex flex-wrap items-center gap-1.5 text-sm">
            <Badge variant="destructive">
              <TriangleAlert aria-hidden="true" />
              Se pasó
            </Badge>
            por <Importe monto={Math.abs(credito.disponible)} moneda={credito.moneda} />
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm">
            Le quedan <Importe monto={credito.disponible} moneda={credito.moneda} />
          </span>
        )}
      </div>

      {credito.cotizacion ? (
        <p className="text-xs text-muted-foreground">
          El límite está en {credito.moneda === "USD" ? "dólares" : "pesos"} y la cuenta lleva el
          saldo en {credito.moneda === "USD" ? "pesos" : "dólares"}: para compararlos se convirtió a{" "}
          <span className="tabular-nums">US$ 1 = $ {textoCotizacion(credito.cotizacion)}</span>.
        </p>
      ) : null}
    </Card>
  );
}

function Contacto({ cliente }: { cliente: Cliente }): JSX.Element {
  const nada = !cliente.telefono && !cliente.email && !cliente.direccion && !cliente.documento;

  return (
    <Card as="section" padding="normal" className="space-y-2">
      <TituloSeccion nivel="etiqueta" as="h3">
        Datos
      </TituloSeccion>
      {nada ? (
        <p className="text-sm text-muted-foreground">
          No hay datos de contacto cargados. Se agregan con «Editar».
        </p>
      ) : (
        <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
          {cliente.telefono ? (
            <DatoIcono icono={Phone} termino="Teléfono">
              <a
                href={`tel:${cliente.telefono.replace(/\s/g, "")}`}
                className="tabular-nums underline-offset-2 hover:underline"
              >
                {cliente.telefono}
              </a>
            </DatoIcono>
          ) : null}
          {cliente.email ? (
            <DatoIcono icono={Mail} termino="Email">
              <a href={`mailto:${cliente.email}`} className="underline-offset-2 hover:underline">
                {cliente.email}
              </a>
            </DatoIcono>
          ) : null}
          {cliente.documento ? (
            <DatoIcono icono={User} termino="DNI o CUIT">
              <Codigo>{cliente.documento}</Codigo>
            </DatoIcono>
          ) : null}
          {cliente.direccion || cliente.localidad ? (
            <DatoIcono icono={MapPin} termino="Dirección">
              {[cliente.direccion, cliente.localidad].filter(Boolean).join(", ")}
            </DatoIcono>
          ) : null}
        </dl>
      )}
      {cliente.observaciones ? (
        <Card variant="muted" padding="compacto" className="text-xs text-muted-foreground">
          {cliente.observaciones}
        </Card>
      ) : null}
    </Card>
  );
}

function Dato({ termino, valor }: { termino: string; valor: string }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">{termino}</dt>
      <dd className="shrink-0 text-right tabular-nums">{valor}</dd>
    </div>
  );
}

function DatoIcono({
  icono: Icono,
  termino,
  children,
}: {
  icono: typeof Phone;
  termino: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start gap-2">
      <Icono className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{termino}</dt>
        <dd className="truncate">{children}</dd>
      </div>
    </div>
  );
}
